import {
  IconArrowBarToDown,
  IconArrowBarUp,
  IconBuildingWarehouse,
  IconCalendarTime,
  IconFileText,
  IconMapPin,
  IconPackage,
  IconStack2,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import type { StockGoods, StockMovement } from "@/utility/stockHistory";
import { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

function formatDateTime(value: string | null) {
  if (!value) return "—";
  return dayjs(value).format("YYYY-MM-DD HH:mm");
}

function TimelineItem({
  movement,
  unit,
  isLast,
}: {
  movement: StockMovement;
  unit: string;
  isLast: boolean;
}) {
  const { t } = useTranslation();
  const isIn = movement.type === "in";

  return (
    <li className="relative flex gap-4 pb-6">
      {!isLast && (
        <span
          aria-hidden
          className="absolute left-[15px] top-8 h-[calc(100%-1rem)] w-px bg-gray-200"
        />
      )}
      <span
        className={`z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          isIn ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
        }`}
      >
        {isIn ? (
          <IconArrowBarToDown size={16} />
        ) : (
          <IconArrowBarUp size={16} />
        )}
      </span>

      <div className="flex-1 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span
              className={`rounded-md px-2 py-0.5 text-xs font-medium ${
                isIn
                  ? "bg-emerald-50 text-emerald-600"
                  : "bg-amber-50 text-amber-600"
              }`}
            >
              {isIn
                ? t("stockHistory.docTypeIn") || "入庫單"
                : t("stockHistory.docTypeOut") || "領料單"}
            </span>
            <span className="text-sm font-medium text-gray-800">
              {movement.docName}
            </span>
          </div>
          <span
            className={`text-lg font-semibold tabular-nums ${
              isIn ? "text-emerald-600" : "text-amber-600"
            }`}
          >
            {isIn ? "+" : "-"}
            {movement.quantity.toLocaleString()}
            {unit && (
              <span className="ml-1 text-xs font-normal text-gray-500">
                {unit}
              </span>
            )}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-xs text-gray-500 sm:grid-cols-2">
          <span className="flex items-center gap-1.5">
            <IconCalendarTime size={14} />
            {formatDateTime(movement.date)}
          </span>
          <span className="flex items-center gap-1.5">
            <IconUser size={14} />
            {isIn
              ? t("stockHistory.deliveryPerson") || "送貨人員"
              : t("stockHistory.pickupPerson") || "領料/出庫"}
            ：{movement.operator}
          </span>
          <span className="flex items-center gap-1.5">
            <IconBuildingWarehouse size={14} />
            {t("stockHistory.warehouseLabel") || "倉庫"}：{movement.warehouse}
          </span>
          <span className="flex items-center gap-1.5">
            <IconMapPin size={14} />
            {movement.area} {t("stockHistory.areaSuffix") || "區"} /{" "}
            {movement.location}
          </span>
        </div>

        {movement.note && (
          <p className="mt-2 flex items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-500">
            <IconFileText size={14} />
            {movement.note}
          </p>
        )}
      </div>
    </li>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? "border-blue-200 bg-blue-50 text-blue-600"
          : "border-gray-200 bg-white text-gray-500 hover:border-blue-200 hover:text-gray-800"
      }`}
    >
      {children}
    </button>
  );
}

function StockMovementDrawer({
  goods,
  onClose,
}: {
  goods: StockGoods | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const open = goods !== null;

  const [locationFilter, setLocationFilter] = useState<string | null>(null);

  useEffect(() => {
    setLocationFilter(null);
  }, [goods?.id]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (open) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const movements = useMemo(() => {
    if (!goods) return [];
    return goods.movements.filter(
      (movement) => !locationFilter || movement.locationKey === locationFilter
    );
  }, [goods, locationFilter]);

  return (
    <div
      className={`fixed inset-0 z-50 ${
        open ? "pointer-events-auto" : "pointer-events-none"
      }`}
      aria-hidden={!open}
    >
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t("stockHistory.drawerLabel") || "貨物進出歷程"}
        className={`absolute right-0 top-0 flex h-full w-full max-w-xl flex-col bg-white shadow-2xl transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {goods && (
          <>
            <header className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <IconPackage size={20} />
                </span>
                <div>
                  <h2 className="text-lg font-semibold leading-tight text-gray-800">
                    {goods.name}
                  </h2>
                  <p className="text-sm text-gray-500">{goods.itemName}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={t("stockHistory.close") || "關閉"}
                className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
              >
                <IconX size={20} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="flex items-center gap-1.5 border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-medium text-gray-800">
                  <IconStack2 size={16} className="text-blue-600" />
                  {t("stockHistory.stockDistribution") || "庫存分佈"}
                  <span className="text-xs font-normal text-gray-500">
                    （{goods.locations.length}{" "}
                    {t("stockHistory.locationCount") || "個儲位"}）
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_auto] gap-x-4 px-4 py-2 text-[11px] font-medium text-gray-500">
                  <span>
                    {t("stockHistory.locationColumn") || "倉庫 / 區 / 儲位"}
                  </span>
                  <span className="text-right">
                    {t("stockHistory.stockLabel") || "庫存"}
                  </span>
                </div>
                <ul className="divide-y divide-gray-200">
                  {goods.locations.map((location) => (
                    <li
                      key={location.key}
                      className="grid grid-cols-[1fr_auto] items-center gap-x-4 px-4 py-2.5 text-sm"
                    >
                      <span className="flex min-w-0 items-center gap-1.5">
                        <IconBuildingWarehouse
                          size={14}
                          className="shrink-0 text-gray-500"
                        />
                        <span className="truncate text-gray-800">
                          {location.warehouse}
                        </span>
                        <span className="shrink-0 text-xs text-gray-500">
                          {location.area} {t("stockHistory.areaSuffix") || "區"}{" "}
                          / {location.location}
                        </span>
                      </span>
                      <span className="text-right font-semibold tabular-nums text-gray-800">
                        {location.stock.toLocaleString()}
                        {goods.unit && (
                          <span className="ml-1 text-xs font-normal text-gray-500">
                            {goods.unit}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                  {goods.locations.length === 0 && (
                    <li className="px-4 py-6 text-center text-sm text-gray-500">
                      {t("stockHistory.noLocation") || "尚無儲位資料"}
                    </li>
                  )}
                </ul>
              </div>

              <div className="mb-4 mt-6 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-800">
                  {t("stockHistory.title") || "進出貨歷程"}
                </h3>
              </div>

              {goods.locations.length > 1 && (
                <div className="mb-4 flex flex-wrap gap-2">
                  <FilterChip
                    active={locationFilter === null}
                    onClick={() => setLocationFilter(null)}
                  >
                    {t("stockHistory.allLocations") || "全部儲位"}
                  </FilterChip>
                  {goods.locations.map((location) => (
                    <FilterChip
                      key={location.key}
                      active={locationFilter === location.key}
                      onClick={() => setLocationFilter(location.key)}
                    >
                      <IconMapPin size={12} />
                      {location.warehouse} · {location.location}
                    </FilterChip>
                  ))}
                </div>
              )}

              <ol className="list-none">
                {movements.map((movement, index) => (
                  <TimelineItem
                    key={movement.id}
                    movement={movement}
                    unit={goods.unit}
                    isLast={index === movements.length - 1}
                  />
                ))}
                {movements.length === 0 && (
                  <li className="rounded-lg border border-dashed border-gray-200 py-8 text-center text-sm text-gray-500">
                    {locationFilter
                      ? t("stockHistory.noMovementForLocation") ||
                        "此儲位沒有進出紀錄"
                      : t("stockHistory.noMovement") || "尚無進出貨紀錄"}
                  </li>
                )}
              </ol>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

export default StockMovementDrawer;
